import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, Shield, Upload, RefreshCcw, BarChart3, Film, Tv, Users, Clock, Star, X, Radio, Award, User2, Check, Ban, Search as SearchIcon, KeyRound, Mail, Crown, Clapperboard, Bell, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Modal, FileField, SubtitleField, AudioTracksField, LANGS, EMPTY_TITLE } from "./shared";

/* ---------------- Catalog ---------------- */
export default function Catalog() {
  const [videos, setVideos] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(EMPTY_TITLE);
  const [uploading, setUploading] = useState({});
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRaw, setBulkRaw] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = () => api.get("/admin/videos").then((r) => setVideos(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (kind, file, lang) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      if (lang) fd.append("lang", lang);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "subtitle") {
        const li = LANGS.find(l => l.code === lang) || { code: lang, label: lang };
        setForm((f) => ({ ...f, subtitles: [...(f.subtitles || []).filter(s => s.lang !== lang), { lang, label: li.label, url: data.url }] }));
        toast.success(`Subtitle (${li.label}) uploaded`);
      } else if (kind === "audio") {
        const li = LANGS.find(l => l.code === lang) || { code: lang, label: lang };
        setForm((f) => ({ ...f, audio_tracks: [...(f.audio_tracks || []).filter(a => a.lang !== lang), { lang, label: li.label, url: data.url }] }));
        toast.success(`Audio track (${li.label}) uploaded`);
      } else {
        const field = kind === "video" ? "stream_url" : kind;
        setForm((f) => ({ ...f, [field]: data.url }));
        toast.success(`${kind} uploaded (${(data.size / 1024 / 1024).toFixed(1)} MB)`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || `${kind} upload failed`); }
    finally { setUploading((u) => ({ ...u, [kind]: false })); }
  };

  const removeSubtitle = (lang) => setForm((f) => ({ ...f, subtitles: (f.subtitles || []).filter(s => s.lang !== lang) }));
  const removeAudio = (lang) => setForm((f) => ({ ...f, audio_tracks: (f.audio_tracks || []).filter(a => a.lang !== lang) }));

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        genres: form.genres.split(",").map(s => s.trim()).filter(Boolean),
        cast: form.cast.split(",").map(s => s.trim()).filter(Boolean),
        year: parseInt(form.year), duration_min: parseInt(form.duration_min),
        tmdb_rating: parseFloat(form.tmdb_rating),
      };
      await api.post("/admin/videos", payload);
      toast.success("Title added");
      setShow(false); setForm(EMPTY_TITLE); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this title (and its episodes)?")) return;
    await api.delete(`/admin/videos/${id}`); load(); toast.success("Deleted");
  };

  const syncTmdb = async () => {
    if (!confirm("Sync latest trending/popular titles from TMDB?")) return;
    const t = toast.loading("Syncing TMDB…");
    try {
      const { data } = await api.post("/admin/tmdb/sync");
      toast.success(`+${data.inserted} new, ${data.updated} updated`, { id: t });
      load();
    } catch { toast.error("Sync failed", { id: t }); }
  };

  const runBulkImport = async () => {
    const lineCount = bulkRaw.split("\n").map((l) => l.trim()).filter(Boolean).length;
    if (!lineCount) { toast.error("Paste at least one TMDB ID"); return; }
    if (lineCount > 50) { toast.error("Max 50 IDs per import"); return; }
    setBulkBusy(true);
    setBulkResult(null);
    const t = toast.loading(`Importing ${lineCount} title(s) from TMDB…`);
    try {
      const { data } = await api.post("/admin/tmdb/bulk-import", { raw: bulkRaw });
      setBulkResult(data);
      toast.success(`+${data.inserted} new · ${data.updated} updated · ${data.failed} failed`, { id: t, duration: 6000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed", { id: t });
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = videos
    .filter(v => filter === "all" ? true : v.type === filter)
    .filter(v => !q || v.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex bg-[#0f0f0f] border border-white/10 rounded p-1">
          {[["all","All"],["movie","Movies"],["series","Series"]].map(([v,l]) => (
            <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 text-sm rounded ${filter === v ? "bg-[#E50914] text-white" : "text-gray-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <input data-testid="catalog-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search catalog…" className="input-dark flex-1 min-w-[200px] max-w-md" />
        <span className="text-sm text-gray-500">{filtered.length} of {videos.length}</span>
        <div className="flex-1" />
        <button data-testid="admin-add-btn" onClick={() => setShow(true)} className="btn-red px-5 py-2 rounded font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Add Title</button>
        <button data-testid="admin-bulk-import-btn" onClick={() => { setBulkResult(null); setBulkOpen(true); }} className="btn-glass px-5 py-2 rounded font-semibold flex items-center gap-2"><Upload className="w-4 h-4" /> Bulk Import</button>
        <button data-testid="admin-tmdb-sync" onClick={syncTmdb} className="btn-glass px-5 py-2 rounded font-semibold flex items-center gap-2"><RefreshCcw className="w-4 h-4" /> Sync TMDB</button>
      </div>

      <div className="bg-[#0f0f0f] border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#1a1a1a] border-b border-white/10 text-gray-400 uppercase text-xs tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Poster</th>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Year</th>
              <th className="px-4 py-3 text-left">Rating</th>
              <th className="px-4 py-3 text-left">Genres</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(v => (
              <tr key={v.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-4 py-2"><img src={v.poster} alt="" className="w-10 h-14 object-cover rounded" /></td>
                <td className="px-4 py-2 font-semibold">{v.title}</td>
                <td className="px-4 py-2 capitalize">{v.type}</td>
                <td className="px-4 py-2">{v.year}</td>
                <td className="px-4 py-2">{v.tmdb_rating}</td>
                <td className="px-4 py-2 text-gray-400 text-xs">{v.genres?.slice(0, 3).join(", ")}</td>
                <td className="px-4 py-2">
                  <button data-testid={`delete-${v.id}`} onClick={() => del(v.id)} className="text-red-500 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <Modal onClose={() => setShow(false)} title="Add Title">
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <input data-testid="admin-title" required placeholder="Title" className="input-dark col-span-2" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            <select data-testid="admin-type" className="input-dark" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
              <option value="movie">Movie</option><option value="series">Series</option>
            </select>
            <input type="number" placeholder="Year" className="input-dark" value={form.year} onChange={e => setForm({...form, year: e.target.value})} />
            <input placeholder="Age rating (PG-13, R)" className="input-dark" value={form.rating} onChange={e => setForm({...form, rating: e.target.value})} />
            <input type="number" step="0.1" placeholder="TMDB Rating" className="input-dark" value={form.tmdb_rating} onChange={e => setForm({...form, tmdb_rating: e.target.value})} />
            <input type="number" placeholder={form.type === "series" ? "Episode runtime (min)" : "Movie duration (min)"} className="input-dark" value={form.duration_min} onChange={e => setForm({...form, duration_min: e.target.value})} />
            <input placeholder="Genres (comma-separated)" className="input-dark" value={form.genres} onChange={e => setForm({...form, genres: e.target.value})} />
            <FileField label="Poster" url={form.poster} onUrl={(u) => setForm({...form, poster: u})} loading={uploading.poster} onFile={(f) => upload("poster", f)} accept="image/*" testid="upload-poster" />
            <FileField label="Backdrop" url={form.backdrop} onUrl={(u) => setForm({...form, backdrop: u})} loading={uploading.backdrop} onFile={(f) => upload("backdrop", f)} accept="image/*" testid="upload-backdrop" />
            {form.type === "movie" && (
              <FileField label="Movie video (mp4)" url={form.stream_url} onUrl={(u) => setForm({...form, stream_url: u})} loading={uploading.video} onFile={(f) => upload("video", f)} accept="video/*" testid="upload-video" />
            )}
            {form.type === "series" && (
              <div className="col-span-2 bg-[#1a0808] border border-[#E50914]/30 rounded p-3 text-sm text-gray-300">
                💡 After saving this series, switch to the <strong>Episodes</strong> tab to upload individual episodes for each season.
              </div>
            )}
            <input placeholder="YouTube Trailer ID" className="input-dark col-span-2" value={form.trailer_yt} onChange={e => setForm({...form, trailer_yt: e.target.value})} />
            <input placeholder="Cast (comma-separated)" className="input-dark col-span-2" value={form.cast} onChange={e => setForm({...form, cast: e.target.value})} />
            <textarea required placeholder="Description" rows={3} className="input-dark col-span-2" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
            <SubtitleField subtitles={form.subtitles} loading={uploading.subtitle} onUpload={upload} onRemove={removeSubtitle} />
            <AudioTracksField tracks={form.audio_tracks} loading={uploading.audio} onUpload={upload} onRemove={removeAudio} />
            <div className="col-span-2 flex gap-3 pt-2">
              <button data-testid="admin-save" type="submit" className="btn-red flex-1 py-3 rounded font-semibold">Save</button>
              <button type="button" onClick={() => setShow(false)} className="btn-glass flex-1 py-3 rounded font-semibold">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {bulkOpen && (
        <Modal onClose={() => !bulkBusy && setBulkOpen(false)} title="Bulk import from TMDB">
          <div data-testid="bulk-import-modal" className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-400 mb-1.5 block">TMDB IDs (one per line · up to 50)</label>
              <textarea
                data-testid="bulk-import-textarea"
                rows={8}
                value={bulkRaw}
                onChange={(e) => setBulkRaw(e.target.value)}
                placeholder={"550\ntv:1399\nmovie:27205\n278\n680"}
                className="input-dark w-full font-mono text-sm"
                disabled={bulkBusy}
              />
              <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
                <strong className="text-gray-400">Format:</strong> just paste numeric IDs (defaults to movie). Prefix <code className="text-amber-300">tv:</code> or <code className="text-amber-300">movie:</code> to switch type — e.g. <code className="text-amber-300">tv:1399</code> for Game of Thrones.<br />
                <strong className="text-gray-400">Find IDs:</strong> The ID is in the TMDB URL — themoviedb.org/movie/<strong className="text-amber-300">550</strong>-fight-club.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                data-testid="bulk-import-run"
                onClick={runBulkImport}
                disabled={bulkBusy || !bulkRaw.trim()}
                className="btn-red flex-1 py-3 rounded font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {bulkBusy ? "Importing…" : "Import to catalog"}
              </button>
              <button
                type="button"
                onClick={() => !bulkBusy && setBulkOpen(false)}
                disabled={bulkBusy}
                className="btn-glass px-6 py-3 rounded font-semibold disabled:opacity-50"
              >
                {bulkResult ? "Close" : "Cancel"}
              </button>
            </div>

            {bulkResult && (
              <div data-testid="bulk-import-result" className="border-t border-white/5 pt-4 space-y-2">
                <div className="flex gap-3 text-sm flex-wrap">
                  <span className="px-2.5 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">+{bulkResult.inserted} new</span>
                  <span className="px-2.5 py-1 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30">{bulkResult.updated} updated</span>
                  {bulkResult.failed > 0 && (
                    <span className="px-2.5 py-1 rounded bg-red-500/10 text-red-300 border border-red-500/30">{bulkResult.failed} failed</span>
                  )}
                </div>
                <ul className="max-h-56 overflow-y-auto bg-black/40 border border-white/5 rounded p-2 space-y-1.5 text-xs">
                  {bulkResult.results.map((r, i) => (
                    <li key={i} data-testid={`bulk-result-row-${i}`} className="flex items-center gap-3">
                      {r.poster ? (
                        <img src={r.poster} alt="" className="w-7 h-10 object-cover rounded border border-white/10" />
                      ) : (
                        <span className="w-7 h-10 rounded border border-red-500/30 bg-red-500/5 flex items-center justify-center text-[10px] text-red-300">✕</span>
                      )}
                      <span className={`w-16 text-[10px] uppercase font-bold tracking-wider ${r.status === "inserted" ? "text-emerald-300" : r.status === "updated" ? "text-amber-300" : "text-red-300"}`}>{r.status}</span>
                      <span className="text-gray-500 w-20 truncate font-mono">{r.media}:{r.tmdb_id}</span>
                      <span className="flex-1 truncate text-gray-200">{r.title || r.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
